import type { FC, ReactNode } from 'react';
import classNames from 'classnames';
import styles from './geometry-views-row.module.scss';

interface GeometryViewsRowProps {
  map: ReactNode;
  explorer: ReactNode;
  /** The View Settings checkboxes. */
  mapVisible: boolean;
  explorerVisible: boolean;
  /** An expanded map takes the whole row; the Explorer steps aside until it shrinks. */
  mapExpanded: boolean;
  /** An expanded Explorer view takes the whole row the same way; the map steps aside. */
  explorerExpanded?: boolean;
  /**
   * One row of three: the map takes a third and the Explorer the remaining two, which it
   * splits between its own two views. The Wellbore Geometry tearsheet reads this way, so the
   * plan view and both depth views are side by side rather than a scroll apart.
   */
  singleRow?: boolean;
}

// The Map beside the Subsurface Explorer (INS-12936): the map says where the wellbore goes and
// the Explorer what it cuts through, so the plan view and both depth views read together rather
// than a scroll apart. Under ~1280px the columns stack full width, where half-width charts stop
// being legible. One column when there is nothing to sit beside.
//
// Both columns hide rather than unmount, so Mapbox and the chart contexts keep their state (and
// their reads) through an expand or a View Settings toggle.
export const GeometryViewsRow: FC<GeometryViewsRowProps> = ({
  map,
  explorer,
  mapVisible,
  explorerVisible,
  mapExpanded,
  explorerExpanded = false,
  singleRow = false,
}) => {
  const showExplorer = explorerVisible && !(mapVisible && mapExpanded);
  const showMap = mapVisible && !(showExplorer && explorerExpanded);
  return (
    <div
      className={classNames(styles.row, {
        [styles.rowSingle]: !showMap || !showExplorer,
        // The taller views are this host's choice, so the token rides the host flag alone —
        // an expanded view collapses the row to one column and must keep the same height it
        // had beside the others, not fall back to the default.
        [styles.rowTall]: singleRow,
        [styles.rowThirds]: singleRow && showMap && showExplorer,
      })}
      data-testid="geometry-views-row"
    >
      <div className={styles.column} hidden={!showMap}>
        {map}
      </div>
      {/* Review item 10: the Explorer's charts shorten to the map's height only while a map
          actually sits beside it; full width (map hidden or expanded) keeps full height. */}
      <div
        className={styles.column}
        hidden={!showExplorer}
        data-beside-map={showMap && showExplorer}
      >
        {explorer}
      </div>
    </div>
  );
};
